<template>
  <b-card no-body>
    <b-card-header class="pb-0">
      <b-card-title>Today's Birthdays & Work Anniversaries</b-card-title>
    </b-card-header>

    <b-card-body>
      <div v-if="!hasAccess" class="text-center py-4">
        <p class="text-muted">Access denied. Admin or HR role required.</p>
      </div>
      
      <div v-else-if="loading" class="d-flex justify-content-center py-4">
        <b-spinner variant="primary" label="Loading..." />
      </div>

      <div v-else-if="error" class="text-center py-4">
        <p class="text-danger">{{ error }}</p>
      </div>

      <div v-else>
        <!-- Birthdays Section -->
        <div class="mb-4" v-if="birthdaysToday && birthdaysToday.length > 0">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Birthdays Today</h6>
            <b-badge variant="primary">{{ birthdaysToday.length }}</b-badge>
          </div>
          <div class="overflow-auto" style="max-height: 15cm">
            <div 
              v-for="(person, index) in birthdaysToday" 
              :key="`birthday-${person.id}`"
              class="d-flex align-items-center py-2 border-bottom"
            >
              <div class="mr-3">
                <b-avatar 
                  v-if="person.profile_image" 
                  :src="person.profile_image" 
                  size="45px"
                  variant="light-primary"
                />
                <b-avatar 
                  v-else 
                  variant="light-primary" 
                  size="45px"
                  :text="getInitials(person.name)"
                />
              </div>
              <div class="flex-grow-1">
                <div class="font-weight-bold">{{ person.name }}</div>
                <div class="text-muted small">
                  <span v-if="person.designation">{{ person.designation }}</span>
                  <span v-if="person.designation && person.department"> • </span>
                  <span v-if="person.department">{{ person.department }}</span>
                </div>
              </div>
              <div class="ml-2">
                <b-button
                  v-if="person.email"
                  :variant="isButtonSent(person.id, 'email', 'birthday') ? 'flat-success' : 'flat-primary'"
                  size="sm"
                  class="mr-1"
                  @click="!isButtonSent(person.id, 'email', 'birthday') && sendEmail(person, 'birthday')"
                  :disabled="isButtonSent(person.id, 'email', 'birthday') || isButtonLoading(person.id, 'email', 'birthday')"
                  v-b-tooltip.hover
                  :title="isButtonSent(person.id, 'email', 'birthday') ? 'Email sent successfully' : 'Send Email'"
                >
                  <b-spinner v-if="isButtonLoading(person.id, 'email', 'birthday')" small class="mr-50" />
                  <feather-icon v-else-if="isButtonSent(person.id, 'email', 'birthday')" icon="CheckIcon" size="16" />
                  <feather-icon v-else icon="MailIcon" size="16" />
                </b-button>
                <b-button
                  v-if="person.mobile"
                  :variant="isButtonSent(person.id, 'whatsapp', 'birthday') ? 'flat-success' : 'flat-success'"
                  size="sm"
                  @click="!isButtonSent(person.id, 'whatsapp', 'birthday') && sendWhatsApp(person, 'birthday')"
                  :disabled="isButtonSent(person.id, 'whatsapp', 'birthday') || isButtonLoading(person.id, 'whatsapp', 'birthday')"
                  v-b-tooltip.hover
                  :title="isButtonSent(person.id, 'whatsapp', 'birthday') ? 'WhatsApp sent successfully' : 'Send WhatsApp'"
                >
                  <b-spinner v-if="isButtonLoading(person.id, 'whatsapp', 'birthday')" small class="mr-50" />
                  <feather-icon v-else-if="isButtonSent(person.id, 'whatsapp', 'birthday')" icon="CheckIcon" size="16" />
                  <feather-icon v-else icon="MessageCircleIcon" size="16" />
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state for birthdays -->
        <div v-else class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Birthdays Today</h6>
            <b-badge variant="secondary">0</b-badge>
          </div>
          <p class="text-muted text-center py-2 small">No birthdays today.</p>
        </div>

        <!-- Work Anniversaries Section -->
        <div v-if="anniversariesToday && anniversariesToday.length > 0">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Work Anniversaries Today</h6>
            <b-badge variant="success">{{ anniversariesToday.length }}</b-badge>
          </div>
          <div class="overflow-auto" style="max-height: 15cm">
            <div 
              v-for="(person, index) in anniversariesToday" 
              :key="`anniversary-${person.id}`"
              class="d-flex align-items-center py-2 border-bottom"
            >
              <div class="mr-3">
                <b-avatar 
                  v-if="person.profile_image" 
                  :src="person.profile_image" 
                  size="45px"
                  variant="light-success"
                />
                <b-avatar 
                  v-else 
                  variant="light-success" 
                  size="45px"
                  :text="getInitials(person.name)"
                />
              </div>
              <div class="flex-grow-1">
                <div class="font-weight-bold">{{ person.name }}</div>
                <div class="text-muted small">
                  <span v-if="person.designation">{{ person.designation }}</span>
                  <span v-if="person.designation && person.department"> • </span>
                  <span v-if="person.department">{{ person.department }}</span>
                </div>
                <div class="text-primary small mt-1">
                  <b-badge variant="success">{{ person.years_completed }} Year{{ person.years_completed > 1 ? 's' : '' }} Completed</b-badge>
                </div>
              </div>
              <div class="ml-2">
                <b-button
                  v-if="person.email"
                  :variant="isButtonSent(person.id, 'email', 'anniversary') ? 'flat-success' : 'flat-primary'"
                  size="sm"
                  class="mr-1"
                  @click="!isButtonSent(person.id, 'email', 'anniversary') && sendEmail(person, 'anniversary')"
                  :disabled="isButtonSent(person.id, 'email', 'anniversary') || isButtonLoading(person.id, 'email', 'anniversary')"
                  v-b-tooltip.hover
                  :title="isButtonSent(person.id, 'email', 'anniversary') ? 'Email sent successfully' : 'Send Email'"
                >
                  <b-spinner v-if="isButtonLoading(person.id, 'email', 'anniversary')" small class="mr-50" />
                  <feather-icon v-else-if="isButtonSent(person.id, 'email', 'anniversary')" icon="CheckIcon" size="16" />
                  <feather-icon v-else icon="MailIcon" size="16" />
                </b-button>
                <b-button
                  v-if="person.mobile"
                  :variant="isButtonSent(person.id, 'whatsapp', 'anniversary') ? 'flat-success' : 'flat-success'"
                  size="sm"
                  @click="!isButtonSent(person.id, 'whatsapp', 'anniversary') && sendWhatsApp(person, 'anniversary')"
                  :disabled="isButtonSent(person.id, 'whatsapp', 'anniversary') || isButtonLoading(person.id, 'whatsapp', 'anniversary')"
                  v-b-tooltip.hover
                  :title="isButtonSent(person.id, 'whatsapp', 'anniversary') ? 'WhatsApp sent successfully' : 'Send WhatsApp'"
                >
                  <b-spinner v-if="isButtonLoading(person.id, 'whatsapp', 'anniversary')" small class="mr-50" />
                  <feather-icon v-else-if="isButtonSent(person.id, 'whatsapp', 'anniversary')" icon="CheckIcon" size="16" />
                  <feather-icon v-else icon="MessageCircleIcon" size="16" />
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state for anniversaries -->
        <div v-else>
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Work Anniversaries Today</h6>
            <b-badge variant="secondary">0</b-badge>
          </div>
          <p class="text-muted text-center py-2 small">No work anniversaries today.</p>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BSpinner,
  BBadge,
  BButton,
  BAvatar,
  VBTooltip,
} from "bootstrap-vue";
import axios from '@/components/axios';

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BSpinner,
    BBadge,
    BButton,
    BAvatar,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      birthdaysToday: [],
      anniversariesToday: [],
      loading: false,
      error: null,
      baseApi: process.env.VUE_APP_APIENDPOINT,
      hasAccess: false,
      loadingButtons: {}, // Track loading state for individual buttons: { "userId-email-birthday": true }
      sentButtons: {}, // Track successfully sent buttons: { "userId-email-birthday": true }
    };
  },
  mounted() {
    // Check user type before making API call
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const userType = userData.type;
    
    // Check user type (case-insensitive)
    const typeLower = (userType || "").toLowerCase();
    
    // Only fetch if user type is admin or contains "hr" (for hr head, hr assistant, etc.)
    if (typeLower === "admin" || typeLower.includes("hr")) {
      this.hasAccess = true;
      this.fetchBirthdayAnniversaryData();
    } else {
      this.hasAccess = false;
      this.error = "Access denied. Admin or HR user type required.";
      console.warn("Birthday Anniversary Widget: User type is", userType, "- Access denied");
    }
  },
  methods: {
    async fetchBirthdayAnniversaryData() {
      this.loading = true;
      this.error = null;

      // Get fresh token on each request
      const accessToken = localStorage.getItem("accessToken");
      
      if (!accessToken) {
        this.error = "Authentication required.";
        this.loading = false;
        return;
      }

      try {
        const requestOptions = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          url: `${this.baseApi}/birthday-anniversary-today`,
        };

        const response = await axios(requestOptions);

        // Check for successful response (status can be 200, true, or truthy)
        if (response.data && (response.data.status === 200 || response.data.status === true || response.status === 200)) {
          // Handle both response formats: response.data.data or response.data
          const data = response.data.data || response.data;
          this.birthdaysToday = data?.birthdays_today || [];
          this.anniversariesToday = data?.anniversaries_today || [];
          
          // Initialize sent buttons from API response flags
          this.initializeSentButtons();
        } else if (response.data && response.data.status === 404) {
          // Handle 403/404 access denied
          if (response.data.message && response.data.message.includes("role required")) {
            this.error = "Access denied. Admin or HR role required.";
          } else {
            this.error = response.data.message || "Error fetching birthday and anniversary data.";
          }
          this.birthdaysToday = [];
          this.anniversariesToday = [];
        } else {
          this.error = response.data?.message || "Error fetching birthday and anniversary data.";
          this.birthdaysToday = [];
          this.anniversariesToday = [];
        }
      } catch (error) {
        console.error("Error fetching birthday and anniversary data:", error);
        console.error("Error response:", error.response);
        
        if (error.response) {
          if (error.response.status === 404) {
            // Endpoint not implemented yet
            this.error = "This feature is not yet available. The birthday and anniversary data endpoint needs to be implemented on the backend.";
            console.warn("Birthday Anniversary API endpoint not found (404). Backend implementation required at: /birthday-anniversary-today");
          } else if (error.response.status === 403) {
            // Access denied
            const errorMessage = error.response.data?.message || "";
            if (errorMessage.includes("role required") || errorMessage.includes("Access denied")) {
              this.error = "Access denied. Admin or HR role required.";
              console.warn("API returned access denied. User role may not match backend requirements.");
            } else {
              this.error = errorMessage || "Access denied. Admin or HR role required.";
            }
          } else if (error.response.data && error.response.data.message) {
            this.error = error.response.data.message;
          } else {
            this.error = "Error fetching birthday and anniversary data.";
          }
        } else {
          this.error = "Error fetching birthday and anniversary data.";
        }
        this.birthdaysToday = [];
        this.anniversariesToday = [];
      } finally {
        this.loading = false;
      }
    },
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    initializeSentButtons() {
      // Initialize sent buttons from API response flags
      // Clear existing sent buttons
      this.sentButtons = {};
      
      // Process birthdays
      this.birthdaysToday.forEach(person => {
        if (person.email_sent === true) {
          this.setButtonSent(person.id, 'email', 'birthday', true);
        }
        if (person.whatsapp_sent === true) {
          this.setButtonSent(person.id, 'whatsapp', 'birthday', true);
        }
      });
      
      // Process anniversaries
      this.anniversariesToday.forEach(person => {
        if (person.email_sent === true) {
          this.setButtonSent(person.id, 'email', 'anniversary', true);
        }
        if (person.whatsapp_sent === true) {
          this.setButtonSent(person.id, 'whatsapp', 'anniversary', true);
        }
      });
    },
    getButtonKey(userId, action, eventType) {
      return `${userId}-${action}-${eventType}`;
    },
    isButtonLoading(userId, action, eventType) {
      const key = this.getButtonKey(userId, action, eventType);
      return this.loadingButtons[key] || false;
    },
    setButtonLoading(userId, action, eventType, isLoading) {
      const key = this.getButtonKey(userId, action, eventType);
      if (isLoading) {
        this.$set(this.loadingButtons, key, true);
      } else {
        this.$delete(this.loadingButtons, key);
      }
    },
    isButtonSent(userId, action, eventType) {
      const key = this.getButtonKey(userId, action, eventType);
      return this.sentButtons[key] || false;
    },
    setButtonSent(userId, action, eventType, isSent) {
      const key = this.getButtonKey(userId, action, eventType);
      if (isSent) {
        this.$set(this.sentButtons, key, true);
      } else {
        this.$delete(this.sentButtons, key);
      }
    },
    async sendEmail(person, eventType) {
      if (!person.email || !person.id) return;

      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        this.$swal({
          title: "Error!",
          text: "Authentication required. Please login again.",
          icon: "error",
          timer: 3000,
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
        });
        return;
      }

      // Set loading state
      this.setButtonLoading(person.id, 'email', eventType, true);

      try {
        const subject = eventType === 'birthday' ? 'Happy Birthday!' : 'Happy Work Anniversary!';
        
        const requestOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          url: `${this.baseApi}/send-birthday-anniversary-email`,
          data: {
            user_id: person.id,
            type: eventType,
            subject: subject,
          },
        };

        const response = await axios(requestOptions);

        if (response.data && response.data.status) {
          // Success - mark button as sent
          this.setButtonSent(person.id, 'email', eventType, true);
          
          const userEmail = response.data.data?.email || person.email;
          this.$swal({
            title: "Success!",
            text: response.data.message || `${eventType === 'birthday' ? 'Birthday' : 'Anniversary'} email sent successfully to ${userEmail}`,
            icon: "success",
            timer: 3000,
            customClass: {
              confirmButton: "btn btn-primary",
            },
            buttonsStyling: false,
          });
        } else {
          // Error response
          this.$swal({
            title: "Error!",
            text: response.data?.message || "Failed to send email. Please try again.",
            icon: "error",
            timer: 5000,
            customClass: {
              confirmButton: "btn btn-primary",
            },
            buttonsStyling: false,
          });
        }
      } catch (error) {
        console.error("Error sending email:", error);
        
        let errorMessage = "Failed to send email. Please try again.";
        
        if (error.response) {
          // Handle 409 Conflict - already sent
          if (error.response.status === 409) {
            const responseData = error.response.data;
            if (responseData && responseData.data && responseData.data.already_sent === true) {
              // Mark as sent and show message
              this.setButtonSent(person.id, 'email', eventType, true);
              errorMessage = responseData.message || "Email has already been sent today.";
              
              this.$swal({
                title: "Already Sent",
                text: errorMessage,
                icon: "info",
                timer: 3000,
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                buttonsStyling: false,
              });
              return; // Exit early, don't show error
            }
          }
          
          if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.status === 403) {
            errorMessage = "Access denied. HR or Admin role required.";
          } else if (error.response.status === 422) {
            errorMessage = "Validation error. Please check the user data.";
          } else if (error.response.status === 400) {
            errorMessage = error.response.data?.message || "Bad request. Please check the data.";
          }
        }

        this.$swal({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          timer: 5000,
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
        });
      } finally {
        // Remove loading state
        this.setButtonLoading(person.id, 'email', eventType, false);
      }
    },
    async sendWhatsApp(person, eventType) {
      if (!person.mobile || !person.id || !person.name) {
        this.$swal({
          title: "Error!",
          text: "Missing required information (mobile, name, or user ID).",
          icon: "error",
          timer: 3000,
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
        });
        return;
      }

      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        this.$swal({
          title: "Error!",
          text: "Authentication required. Please login again.",
          icon: "error",
          timer: 3000,
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
        });
        return;
      }

      // Set loading state
      this.setButtonLoading(person.id, 'whatsapp', eventType, true);

      try {
        // Build payload according to API documentation
        const payload = {
          type: eventType, // 'birthday' or 'anniversary'
          user_id: person.id,
          name: person.name,
          mobile: person.mobile,
        };

        // Add years only for anniversary type
        if (eventType === 'anniversary' && person.years_completed) {
          payload.years = `${person.years_completed} year${person.years_completed > 1 ? 's' : ''}`;
        }

        // Build endpoint URL - handle both cases where baseApi includes /api or not
        const baseApiWithoutApi = this.baseApi.replace(/\/api$/, ''); // Remove trailing /api if present
        const endpoint = `${baseApiWithoutApi}/api/send-birthday-anniversary-whatsapp`;

        const requestOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          url: endpoint,
          data: payload,
        };

        const response = await axios(requestOptions);

        // Check both response.status and data.status for success
        if (response.status === 200 && response.data && response.data.status === true) {
          // Success - mark button as sent
          this.setButtonSent(person.id, 'whatsapp', eventType, true);
          
          const successMessage = response.data.message || `${eventType === 'birthday' ? 'Birthday' : 'Anniversary'} WhatsApp message sent successfully.`;
          
          // Log template used for debugging
          if (response.data.template) {
            console.log('WhatsApp template used:', response.data.template);
          }

          this.$swal({
            title: "Success!",
            text: successMessage,
            icon: "success",
            timer: 3000,
            customClass: {
              confirmButton: "btn btn-primary",
            },
            buttonsStyling: false,
          });
        } else {
          // Error response from API
          this.$swal({
            title: "Error!",
            text: response.data?.message || "Failed to send WhatsApp message. Please try again.",
            icon: "error",
            timer: 5000,
            customClass: {
              confirmButton: "btn btn-primary",
            },
            buttonsStyling: false,
          });
        }
      } catch (error) {
        console.error("Error sending WhatsApp:", error);
        
        let errorMessage = "Failed to send WhatsApp message. Please try again.";
        let errorTitle = "Error!";
        let errorIcon = "error";
        
        if (error.response) {
          const errorData = error.response.data || {};
          const statusCode = error.response.status;

          // Handle different error status codes according to API documentation
          switch (statusCode) {
            case 401:
              // Unauthorized - User not authenticated
              errorMessage = errorData.message || "User not authenticated. Please login again.";
              errorTitle = "Authentication Required";
              // Redirect to login (handled by axios interceptor, but show message)
              break;

            case 403:
              // Access Denied - Admin or HR role required
              errorMessage = errorData.message || "Access denied. Admin or HR role required.";
              errorTitle = "Access Denied";
              console.warn('Access denied. User role:', errorData.user_role, 'User type:', errorData.user_type);
              break;

            case 409:
              // Conflict - Already sent today
              // Mark as sent and show warning (not error)
              this.setButtonSent(person.id, 'whatsapp', eventType, true);
              errorMessage = errorData.message || "WhatsApp message has already been sent today.";
              errorTitle = "Already Sent";
              errorIcon = "warning";
              
              this.$swal({
                title: errorTitle,
                text: errorMessage,
                icon: errorIcon,
                timer: 3000,
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                buttonsStyling: false,
              });
              return; // Exit early, don't show error

            case 422:
              // Validation Error
              errorMessage = errorData.message || "Validation error. Please check the user data.";
              if (errorData.validation_errors) {
                // Show validation errors
                const validationMessages = Object.values(errorData.validation_errors)
                  .flat()
                  .join(', ');
                errorMessage = `Validation failed: ${validationMessages}`;
              }
              errorTitle = "Validation Error";
              break;

            case 400:
              // Bad Request - WhatsApp send failure
              errorMessage = errorData.message || "Failed to send WhatsApp message.";
              errorTitle = "Send Failed";
              
              // Log debugging information
              if (errorData.whatsapp_error) {
                console.error('WhatsApp API Error:', errorData.whatsapp_error);
              }
              if (errorData.whatsapp_template_used) {
                console.error('Template attempted:', errorData.whatsapp_template_used);
              }
              if (errorData.mobile_number) {
                console.error('Mobile number used:', errorData.mobile_number);
              }
              break;

            case 500:
              // Server Error
              errorMessage = errorData.message || "Server error. Please try again later.";
              errorTitle = "Server Error";
              
              // Log error details for debugging
              if (errorData.error_type) {
                console.error('Error type:', errorData.error_type);
                console.error('Error file:', errorData.file);
                console.error('Error line:', errorData.line);
              }
              break;

            default:
              // Other errors
              errorMessage = errorData.message || "An unexpected error occurred. Please try again.";
              break;
          }
        } else if (error.request) {
          // Network error
          errorMessage = "Network error. Please check your connection and try again.";
          errorTitle = "Network Error";
        }

        this.$swal({
          title: errorTitle,
          text: errorMessage,
          icon: errorIcon,
          timer: 5000,
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
        });
      } finally {
        // Remove loading state
        this.setButtonLoading(person.id, 'whatsapp', eventType, false);
      }
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e0e0e0 !important;
}

.border-bottom:last-child {
  border-bottom: none !important;
}
</style>

